import React, { useEffect, useRef, useState } from "react";
import logger from "../lib/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Eyebrow } from "../components/Primitives";
import { api } from "../lib/api";
import VoiceRecorderButton from "../components/VoiceRecorderButton";
import ListenButton from "../components/ListenButton";
import LiveDateIdeas from "../components/LiveDateIdeas";

const MODES = {
    coach: {
        title: "Dating & Romance Coach",
        eyebrow: "AI · Coach",
        italic: "Your",
        rest: "in-pocket relationship guide.",
        placeholder: "What's on your mind?",
        suggestions: [
            "Why do I keep falling for emotionally unavailable people?",
            "I want to bring more romance back. Where do I start?",
            "How do I have a hard conversation without making it worse?",
            "I'm anxious he hasn't texted back. Help me stay grounded.",
        ],
        empty: "Tell me what's happening in your dating life and I'll help you think it through.",
    },
    concierge: {
        title: "Dating Concierge",
        eyebrow: "AI · Concierge",
        italic: "Your",
        rest: "white-glove dating assistant.",
        placeholder: "What can I take care of?",
        suggestions: [
            "Draft an opening message for my new match.",
            "Plan a Tuesday-night first date in my city.",
            "Help me write a polite no-thanks message.",
            "Rewrite my bio to feel a little warmer.",
        ],
        empty: "Tell me what you need handled — a message, a date, a tricky reply — and I'll take care of it.",
    },
};

export default function AIChat({ user, mode, onBack }) {
    const cfg = MODES[mode];
    const [items, setItems] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.aiHistory(user.session_id, mode);
                setItems(r.items || []);
            } catch (err) {
                logger.error("aiHistory load failed:", err);
            }
            setLoading(false);
        })();
    }, [user.session_id, mode]);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [items, sending]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setInput("");
        setError(null);
        // Optimistic user bubble
        const tempId = `tmp-${Date.now()}`;
        setItems(prev => [...prev, { id: tempId, role: "user", content: msg }]);
        setSending(true);
        try {
            const r = await api.aiChat(user.session_id, mode, msg);
            setItems(prev => {
                // Replace the temp user bubble with the persisted one + add the assistant reply
                const cleaned = prev.filter(p => p.id !== tempId);
                return [...cleaned, r.user, r.assistant];
            });
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't reach the AI. Try again.");
            // Remove optimistic bubble on failure
            setItems(prev => prev.filter(p => p.id !== tempId));
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-cream h-screen max-h-screen overflow-hidden" data-testid={`ai-${mode}-screen`}>
            <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-burgundy/10">
                <button
                    data-testid="ai-back-btn"
                    onClick={onBack}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">{cfg.eyebrow}</span>
                <span className="w-8" />
            </div>

            <div className="px-7 pt-5 pb-3">
                <Eyebrow>Beta</Eyebrow>
                <h1 className="mt-1 font-display text-3xl text-burgundy leading-[1.1]">
                    <span className="italic">{cfg.italic}</span> {cfg.rest}
                </h1>
            </div>

            <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-3"
                data-testid="ai-chat-scroll"
            >
                {loading && (
                    <div className="text-center text-xs tracking-[0.24em] uppercase text-ink-muted py-10">
                        Loading…
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <div className="py-4">
                        <p className="font-display italic text-base text-ink-muted leading-snug">
                            {cfg.empty}
                        </p>
                        <div className="mt-4 space-y-2">
                            {cfg.suggestions.map((s, i) => (
                                <button
                                    key={s}
                                    data-testid={`ai-suggestion-${i}`}
                                    onClick={() => send(s)}
                                    disabled={sending}
                                    className="block w-full text-left text-sm border border-burgundy/15 hover:border-burgundy/40 bg-cream px-4 py-3 text-ink leading-snug transition-colors disabled:opacity-50"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        {mode === "concierge" && <LiveDateIdeas sessionId={user.session_id} className="mt-4" />}
                    </div>
                )}

                <AnimatePresence initial={false}>
                    {items.map((m) => (
                        <motion.div
                            key={m.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25 }}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            data-testid={`ai-bubble-${m.role}`}
                        >
                            <div className={`max-w-[85%] flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                                <div
                                    className={`px-4 py-2.5 text-sm leading-snug whitespace-pre-line ${
                                        m.role === "user"
                                            ? "bg-burgundy text-cream rounded-bl-xl rounded-tr-xl rounded-tl-xl"
                                            : "bg-cream-soft border border-burgundy/15 text-ink rounded-br-xl rounded-tr-xl rounded-tl-xl"
                                    }`}
                                >
                                    {m.content}
                                </div>
                                {m.role === "assistant" && (
                                    <ListenButton sessionId={user.session_id} text={m.content} testId={`ai-listen-${m.id}`} className="-mb-2" />
                                )}
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {sending && (
                    <div className="flex justify-start" data-testid="ai-typing">
                        <div className="bg-cream-soft border border-burgundy/15 px-4 py-2.5 rounded-br-xl rounded-tr-xl rounded-tl-xl">
                            <div className="flex gap-1">
                                {[0, 1, 2].map(i => (
                                    <motion.span
                                        key={`dot-${i}`}
                                        className="w-1.5 h-1.5 rounded-full bg-burgundy/60"
                                        animate={{ opacity: [0.3, 1, 0.3] }}
                                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="text-xs text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2" data-testid="ai-error">
                        {error}
                    </div>
                )}
            </div>

            <div className="border-t border-burgundy/15 px-4 pt-3 pb-safe-sm bg-cream-soft">
                <div className="flex items-end gap-2">
                    <textarea
                        ref={inputRef}
                        data-testid="ai-input"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={onKey}
                        placeholder={cfg.placeholder}
                        rows={1}
                        className="flex-1 bg-cream border border-burgundy/20 focus:border-burgundy py-2.5 px-3 text-base text-ink resize-none focus:outline-none max-h-32"
                        style={{ minHeight: 44 }}
                    />
                    <VoiceRecorderButton
                        sessionId={user.session_id}
                        disabled={sending}
                        onText={(t) => { setInput(prev => (prev ? `${prev.trim()} ${t}` : t)); inputRef.current?.focus(); }}
                    />
                    <button
                        data-testid="ai-send-btn"
                        onClick={() => send()}
                        disabled={sending || !input.trim()}
                        className="bg-burgundy text-cream uppercase text-[10px] tracking-[0.22em] px-4 min-h-[44px] hover:bg-burgundy-deep transition-colors disabled:opacity-40 shrink-0"
                    >
                        Send
                    </button>
                </div>
                <p className="mt-2 text-[10px] tracking-[0.22em] uppercase text-ink-light text-center">
                    AI advice · not a substitute for professional care
                </p>
            </div>
        </div>
    );
}
